import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { useContent } from '../../context/ContentContext';
import {
  DEFAULT_HOME_SERMONS_MORE_LINK,
  resolveHomeSermons,
} from '../../data/homeSermons';
import VideoEditModal from './VideoEditModal';
import './editable.css';

export default function EditableHomeSermons() {
  const { isEditing } = useAuth();
  const { controlsVisible } = useEditMode();
  const { getContent, setContentValue } = useContent();
  const sermons = resolveHomeSermons(getContent);
  const moreLink = getContent('home.sermons.moreLink', DEFAULT_HOME_SERMONS_MORE_LINK);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingMoreLink, setEditingMoreLink] = useState(false);
  const [moreLinkDraft, setMoreLinkDraft] = useState(moreLink);

  const saveSermons = (nextSermons) => {
    setContentValue('home.sermons.items', nextSermons);
  };

  const updateSermon = (index, patch) => {
    saveSermons(sermons.map((sermon, idx) => (idx === index ? { ...sermon, ...patch } : sermon)));
  };

  const removeSermon = (index) => {
    saveSermons(sermons.filter((_, idx) => idx !== index));
    setEditingIndex(null);
  };

  const addSermon = () => {
    const url = window.prompt('YouTube URL for new sermon');
    if (!url?.trim()) return;
    saveSermons([
      ...sermons,
      {
        url: url.trim(),
        thumbnail: '',
        alt: `Sermon ${sermons.length + 1}`,
      },
    ]);
  };

  const activeVideo = editingIndex !== null ? { ...sermons[editingIndex], index: editingIndex } : null;

  return (
    <>
      <div className="videos-grid" data-content-key="home.sermons.items">
        {sermons.map((sermon, index) => (
          <div
            key={`${sermon.url}-${index}`}
            className={`video-container${isEditing ? ' video-container--editable' : ''}`}
            data-aos="fade-up"
            data-aos-delay={100 + index * 50}
          >
            <a
              href={sermon.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={isEditing ? (e) => e.preventDefault() : undefined}
            >
              <img src={sermon.thumbnail} alt={sermon.alt} />
              <div className="play-icon" />
            </a>
            {isEditing && (
              <button
                type="button"
                className="video-edit-trigger"
                aria-label={`Edit sermon ${index + 1}`}
                onClick={() => setEditingIndex(index)}
              >
                Edit
              </button>
            )}
          </div>
        ))}
        {isEditing && controlsVisible && (
          <button type="button" className="video-edit-add" onClick={addSermon}>
            <span className="video-edit-add-icon">+</span>
            <span className="video-edit-add-label">Add sermon</span>
          </button>
        )}
      </div>

      <div className="view-more-container" data-aos="fade-up" data-aos-delay="300">
        {isEditing ? (
          <div className="view-more-edit-wrap" data-content-key="home.sermons.moreLink">
            <a
              href={moreLink}
              target="_blank"
              rel="noopener noreferrer"
              className="view-more-button"
              onClick={(e) => e.preventDefault()}
            >
              View More
            </a>
            <button
              type="button"
              className="video-edit-trigger video-edit-trigger--inline"
              onClick={() => {
                setMoreLinkDraft(moreLink);
                setEditingMoreLink(true);
              }}
            >
              Edit link
            </button>
          </div>
        ) : (
          <a href={moreLink} target="_blank" rel="noopener noreferrer" className="view-more-button">
            View More
          </a>
        )}
      </div>

      {activeVideo && (
        <VideoEditModal
          title={`Edit sermon ${activeVideo.index + 1}`}
          video={activeVideo}
          onClose={() => setEditingIndex(null)}
          onSaveUrl={(url) => {
            updateSermon(activeVideo.index, { url });
            setEditingIndex(null);
          }}
          onSaveThumbnail={(thumbnail) => updateSermon(activeVideo.index, { thumbnail })}
          onRemove={() => removeSermon(activeVideo.index)}
        />
      )}

      {editingMoreLink && (
        <div className="video-edit-modal-overlay" onClick={() => setEditingMoreLink(false)} role="presentation">
          <div
            className="video-edit-modal video-edit-modal--compact"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="sermon-more-link-title"
          >
            <button
              type="button"
              className="video-edit-modal-close"
              onClick={() => setEditingMoreLink(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 id="sermon-more-link-title" className="video-edit-modal-title">
              View more link
            </h3>
            <label className="video-edit-modal-label" htmlFor="sermon-more-link-url">
              YouTube channel or playlist URL
            </label>
            <input
              id="sermon-more-link-url"
              type="url"
              className="video-edit-modal-input"
              value={moreLinkDraft}
              onChange={(e) => setMoreLinkDraft(e.target.value)}
              placeholder="https://www.youtube.com/..."
            />
            <div className="video-edit-modal-actions">
              <button
                type="button"
                className="video-edit-modal-btn"
                onClick={() => {
                  if (moreLinkDraft.trim()) {
                    setContentValue('home.sermons.moreLink', moreLinkDraft.trim());
                  }
                  setEditingMoreLink(false);
                }}
              >
                Save link
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
